/*
  Cookie consent state — the single source of truth for what we are allowed to load.

  Modelled on the EU requirements (GDPR Art. 4(11) + 7, ePrivacy Directive Art. 5(3))
  as interpreted by the EDPB:

    - Prior consent: nothing non-essential loads before the visitor agrees.
      This is why Consent Mode defaults to denied in index.html, not here.
    - Freely given: "Reject all" is one click, same prominence as "Accept all".
      A banner where refusing is harder than accepting is not valid consent.
    - Specific: analytics and marketing are separate choices.
    - Unambiguous: nothing is pre-ticked; no "continuing to browse means yes".
    - Withdrawable: the choice can be reopened and changed at any time.
    - Demonstrable: we store what was agreed, the wording version, and when.

  Strictly necessary cookies (session, security) are exempt and never gated.
*/

const KEY = 'eagentix_consent';

// Bump when the banner wording or categories change — a stored consent against
// an older version is no longer evidence of consent to the new terms.
export const CONSENT_VERSION = 1;

export const DENY_ALL = { analytics: false, marketing: false };
export const ALLOW_ALL = { analytics: true, marketing: true };

/** Stored decision, or null if the visitor has not chosen yet. */
export function getConsent() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    // Re-ask if the terms changed since they agreed.
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

export function hasDecided() {
  return getConsent() !== null;
}

/** Push the decision into Google Consent Mode v2. */
function syncGoogle(consent) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  const ad = consent.marketing ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    ad_storage: ad,
    ad_user_data: ad,
    ad_personalization: ad,
    analytics_storage: consent.analytics ? 'granted' : 'denied',
  });
}

/**
 * Record a decision and apply it immediately.
 * `wording` is stored as evidence of what was actually agreed to.
 */
export function setConsent({ analytics, marketing }, wording = '') {
  const record = {
    analytics: Boolean(analytics),
    marketing: Boolean(marketing),
    version: CONSENT_VERSION,
    wording,
    decided_at: new Date().toISOString(),
  };
  try {
    localStorage.setItem(KEY, JSON.stringify(record));
  } catch (_) {
    /* storage blocked — the session still honours the choice in memory */
  }
  syncGoogle(record);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('eagentix:consent', { detail: record }));
  }
  return record;
}

/** Clear the decision so the banner reappears (used by "Cookie settings"). */
export function resetConsent() {
  try {
    localStorage.removeItem(KEY);
  } catch (_) {
    /* no-op */
  }
  syncGoogle(DENY_ALL);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('eagentix:consent', { detail: null }));
  }
}

/** Has the visitor allowed a given category? Defaults to no. */
export function allows(category) {
  const c = getConsent();
  return Boolean(c && c[category]);
}
